/**
 * PSX trading hours (client-side mirror of apps/api/src/lib/marketHours.ts).
 *
 *   Mon–Thu  09:30–15:30 PKT
 *   Fri      09:15–12:00 and 14:30–16:30 PKT (Jumma break)
 *
 * Used for the LIVE / MARKET CLOSED badges. Public holidays aren't modelled;
 * the badge degrades gracefully and the worker keeps last-known prices.
 */
const PKT_OFFSET_MINUTES = 5 * 60;

export type MarketStatus = 'open' | 'closed';

const WEEKDAY = [[9 * 60 + 30, 15 * 60 + 30]];
const FRIDAY = [
  [9 * 60 + 15, 12 * 60],
  [14 * 60 + 30, 16 * 60 + 30],
];

export function getMarketStatus(now: Date = new Date()): MarketStatus {
  // Convert to PKT regardless of viewer timezone.
  const pkt = new Date(now.getTime() + (now.getTimezoneOffset() + PKT_OFFSET_MINUTES) * 60_000);
  const day = pkt.getDay(); // 0=Sun, 6=Sat
  if (day === 0 || day === 6) return 'closed';
  const minutes = pkt.getHours() * 60 + pkt.getMinutes();
  const sessions = day === 5 ? FRIDAY : WEEKDAY;
  return sessions.some(([s, e]) => minutes >= s && minutes < e) ? 'open' : 'closed';
}

/**
 * True once that weekday's FIRST session has begun — not necessarily still
 * open. Decides whether "today" has any market data worth ranking, so the
 * dashboard can show today's movers during the Jumma break and after the
 * close, but last session's before the bell. Uses the real per-day open
 * (09:30 Mon-Thu, 09:15 Fri) rather than one hardcoded time.
 */
/** What the market does next, and when. */
export interface MarketCountdown {
  status: MarketStatus;
  /**
   * `closes` ends the trading day; `breaks` is Friday's Jumma pause, which is
   * not the close and must not read like one. `reopens` is the far side of
   * that pause; `opens` is a new trading day.
   */
  kind: 'opens' | 'closes' | 'breaks' | 'reopens';
  /** When it happens, in real time — safe to compare against Date.now(). */
  at: Date;
  msUntil: number;
}

/** Minutes-of-day sessions for a PKT weekday; empty at the weekend. */
function sessionsFor(day: number): number[][] {
  if (day === 0 || day === 6) return [];
  return day === 5 ? FRIDAY : WEEKDAY;
}

/**
 * The next boundary the market crosses.
 *
 * Everything is computed on a PKT wall clock so the answer is the same for a
 * viewer in Karachi and one in London. Public holidays are not modelled — on
 * one the countdown will run to a session that does not happen, the same
 * limitation the status badge already has.
 */
export function getMarketCountdown(now: Date = new Date()): MarketCountdown {
  const shift = (now.getTimezoneOffset() + PKT_OFFSET_MINUTES) * 60_000;
  const pkt = new Date(now.getTime() + shift);
  const minutes = pkt.getHours() * 60 + pkt.getMinutes() + pkt.getSeconds() / 60;
  const today = sessionsFor(pkt.getDay());

  /** A PKT wall-clock time, `dayOffset` days out, back in real time. */
  const realAt = (dayOffset: number, minuteOfDay: number): Date => {
    const t = new Date(pkt);
    t.setDate(pkt.getDate() + dayOffset);
    t.setHours(Math.floor(minuteOfDay / 60), minuteOfDay % 60, 0, 0);
    return new Date(t.getTime() - shift);
  };
  const done = (status: MarketStatus, kind: MarketCountdown['kind'], at: Date): MarketCountdown => ({
    status,
    kind,
    at,
    msUntil: Math.max(0, at.getTime() - now.getTime()),
  });

  // Inside a session: the next boundary is its end — the day's close, or the
  // Jumma break when another session follows it today.
  const index = today.findIndex(([s, e]) => minutes >= s && minutes < e);
  if (index >= 0) {
    const isLast = index === today.length - 1;
    return done('open', isLast ? 'closes' : 'breaks', realAt(0, today[index][1]));
  }

  // Between sessions today: the pause ends rather than the day beginning.
  const later = today.find(([s]) => minutes < s);
  if (later) {
    const alreadyTraded = today.some(([, e]) => minutes >= e);
    return done('closed', alreadyTraded ? 'reopens' : 'opens', realAt(0, later[0]));
  }

  // Otherwise the next trading day. Seven hops always finds one.
  for (let offset = 1; offset <= 7; offset++) {
    const day = (pkt.getDay() + offset) % 7;
    const sessions = sessionsFor(day);
    if (sessions.length) return done('closed', 'opens', realAt(offset, sessions[0][0]));
  }
  // Unreachable: every week contains a weekday.
  return done('closed', 'opens', realAt(1, WEEKDAY[0][0]));
}

export function sessionStartedToday(now: Date = new Date()): boolean {
  const pkt = new Date(now.getTime() + (now.getTimezoneOffset() + PKT_OFFSET_MINUTES) * 60_000);
  const day = pkt.getDay();
  if (day === 0 || day === 6) return false;
  const sessions = day === 5 ? FRIDAY : WEEKDAY;
  return pkt.getHours() * 60 + pkt.getMinutes() >= sessions[0][0];
}
